// A tab left open across a deploy still runs the previous build, whose lazy chunks are gone from the server (the
// hosting rewrite answers them with index.html). On the first chunk-load error the page reloads to pick up the new
// build. The sessionStorage guard allows one reload per build in a tab, so a chunk that is also missing from the new
// build cannot start a reload loop: a page that loads under the guard of its own build never reloads. Only the page
// that started the reload may try again, if it is still here after the cancel window (the reload was cancelled, or is
// slow), and the count caps a build at two reloads per tab, whatever the timing.
//
// This module imports nothing from the app: the modules that load chunks, and their tests, import it and must not pull
// in the store through logUtils.

// The build that reloaded: `<commit>.<Amplify job>` on Amplify, else the fallback in currentBuild. The grin-automation
// e2e suite reads this value: keep it a plain build id (the count has its own key).
const RELOAD_GUARD_KEY = 'chunkLoadReloadBuild'
// Reloads started for that build in this tab.
const RELOAD_COUNT_KEY = 'chunkLoadReloadCount'
const MAX_RELOADS_PER_BUILD = 2

// How a failed dynamic import reads: native modules in Chromium, Firefox and Safari, Vite's CSS preload, and the
// SystemJS loader of the legacy build (a script that failed to load, or index.html served in its place).
const CHUNK_LOAD_ERROR = new RegExp(
  [
    'Failed to fetch dynamically imported module',
    'error loading dynamically imported module',
    'Importing a module script failed',
    'is not a valid JavaScript MIME type',
    'Unable to preload CSS',
    "Unexpected token '<'",
    "expected expression, got '<'",
    'systemjs/blob/main/docs/errors\\.md#[23]\\b'
  ].join('|'),
  'i'
)

let reloading = false
// This page started a reload. If it is still here after the cancel window (cancelled, or slow), its own guard does not
// stop it; the count does.
let reloadStartedHere = false

// A reload that does not happen within this time was cancelled (a `beforeunload` prompt kept the page): this page may
// reload again on the next chunk-load error, and the import that waited for the reload fails as it would have. A slow
// reload looks the same from here, but the page it loads finds the guard in place.
const RELOAD_CANCELLED_MS = 3000

// Amplify Hosting rebuilds the same commit on a redeploy (new chunk hashes), so the job id is part of the key. Local
// builds have neither and fall back to the package version, which does not change between releases: there a tab gets
// one reload in all.
const currentBuild = () =>
  [process.env.AWS_COMMIT_ID, process.env.AWS_JOB_ID].filter(Boolean).join('.') ||
  process.env.REACT_APP_VERSION ||
  'unknown'

export const isChunkLoadError = error => CHUNK_LOAD_ERROR.test(String(error?.message ?? error ?? ''))

/**
 * Reloads the page for a chunk-load error, once per build in this tab (twice if the page that started the first reload
 * is still here after the cancel window).
 * Returns true while a reload is under way, so the caller can wait for it instead of failing.
 */
export const reloadOnChunkLoadError = error => {
  if (!isChunkLoadError(error)) {
    return false
  }
  if (reloading) {
    return true
  }
  // Offline, every chunk fails; a reload would only land on the browser's offline page. Let the error surface.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return false
  }

  try {
    const build = currentBuild()
    const guarded = sessionStorage.getItem(RELOAD_GUARD_KEY) === build
    // A guard with a missing or unreadable count counts as one reload.
    const reloads = guarded ? Number(sessionStorage.getItem(RELOAD_COUNT_KEY)) || 1 : 0
    if (guarded && (!reloadStartedHere || reloads >= MAX_RELOADS_PER_BUILD)) {
      return false
    }
    sessionStorage.setItem(RELOAD_GUARD_KEY, build)
    sessionStorage.setItem(RELOAD_COUNT_KEY, String(reloads + 1))
  } catch (e) {
    // Without storage there is no guard, so never reload.
    return false
  }

  reloading = true
  reloadStartedHere = true
  // Only the in-memory flag resets. The sessionStorage guard and count are never released: a timer cannot tell a
  // cancelled reload from a slow one.
  setTimeout(() => {
    reloading = false
  }, RELOAD_CANCELLED_MS)
  window.location.reload()
  return true
}

/**
 * Wraps a dynamic import so that a chunk-load error reloads the page (see reloadOnChunkLoadError) instead of failing,
 * e.g. React.lazy(() => importWithReload(() => import('./Page'))).
 * While the reload is under way the import stays pending; if the page is still here after the cancel window, it
 * rejects with the original error.
 */
export const importWithReload = load =>
  load().catch(error =>
    reloadOnChunkLoadError(error)
      ? new Promise((resolve, reject) => setTimeout(() => reject(error), RELOAD_CANCELLED_MS))
      : Promise.reject(error)
  )

/**
 * Vite reports a failed preload or dynamic import of the built app as a `vite:preloadError` event on window.
 * It fires alongside importWithReload for the import() calls in src (the `reloading` flag makes the two one reload),
 * and covers the import() calls of dependencies, which do not go through it.
 */
export const registerChunkLoadErrorReload = () => {
  window.addEventListener('vite:preloadError', event => reloadOnChunkLoadError(event.payload))
}
